import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Services.css';

const Services: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="services-page">
      <div className="services-hero">
        <motion.div 
          className="services-hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Service Times</h1>
          <p>Join us for worship, prayer, and fellowship—all are welcome!</p>
          <p className="hero-subtext">Find your place in our community</p>
        </motion.div>
      </div>

      <div className="services-container">
        <motion.div 
          className="services-grid"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div className="service-card sunday-service" variants={fadeInUp}>
            <div className="service-day">
              <h2>Sunday</h2>
              <p className="service-day-subtitle">Start your week with purpose</p>
            </div>
            <div className="service-items">
              <div className="service-item">
                <div className="service-icon">🙏</div>
                <div className="service-details">
                  <h3>Corporate Prayer</h3>
                  <p className="service-time">8:00 AM</p>
                  <p className="service-description">
                    Begin your week in prayer. Join us for a powerful time of focused prayer, 
                    intercession, and seeking God's presence together.
                  </p>
                  <span className="service-note">All ages welcome • No experience necessary</span>
                </div>
              </div>
              <div className="service-item">
                <div className="service-icon">🎵</div>
                <div className="service-details">
                  <h3>Worship & The Word</h3>
                  <p className="service-time">9:00 AM</p>
                  <p className="service-description">
                    Our main service featuring passionate worship, inspiring music, and practical 
                    Bible teaching that applies to everyday life. Perfect for the whole family!
                  </p>
                  <span className="service-note">Typically 90 minutes • Children welcome</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div className="service-card wednesday-service" variants={fadeInUp}>
            <div className="service-day">
              <h2>Wednesday</h2>
              <p className="service-day-subtitle">Mid-week spiritual refreshment</p>
            </div>
            <div className="service-items">
              <div className="service-item">
                <div className="service-icon">🙏</div>
                <div className="service-details">
                  <h3>Corporate Prayer</h3>
                  <p className="service-time">6:00 PM</p>
                  <p className="service-description">
                    Recharge your spirit mid-week. Join us for an evening of prayer, encouragement, 
                    and fellowship to help you finish your week strong.
                  </p>
                  <span className="service-note">Perfect for busy schedules</span>
                </div>
              </div>
              <div className="service-item">
                <div className="service-icon">📖</div>
                <div className="service-details">
                  <h3>Journey Life Application Bible Study (JLABS)</h3>
                  <p className="service-time">7:00 PM - 8:15 PM</p>
                  <p className="service-description">
                    Go deeper into God's Word with our interactive Bible study. We explore Scripture 
                    together and discover how to apply biblical principles to your daily life, 
                    relationships, and challenges.
                  </p>
                  <span className="service-note">Interactive discussion • Questions encouraged</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div 
          className="location-section"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeInUp}>Find Us</motion.h2>
          <motion.div className="location-card" variants={fadeInUp}>
            <div className="location-info">
              <div className="location-item">
                <div className="location-icon">📍</div>
                <strong>Address</strong>
                <p>9644 Maumelle Blvd<br />North Little Rock, AR 72113</p>
                <span className="location-note">Plenty of parking available</span>
              </div>
              <div className="location-item">
                <div className="location-icon">📞</div>
                <strong>Phone</strong>
                <p><a href="tel:+15013530651">(501) 353-0651</a></p>
                <span className="location-note">Call us anytime with questions</span>
              </div>
              <div className="location-item">
                <div className="location-icon">📧</div>
                <strong>Email</strong>
                <p><a href="mailto:admin@gener8ionschurchonline.org">admin@gener8ionschurchonline.org</a></p>
                <span className="location-note">We'll respond within 24 hours</span>
              </div>
            </div>
            <div className="map-container">
              <iframe
                src="https://maps.google.com/maps?q=9644+Maumelle+Blvd,+North+Little+Rock,+AR+72113&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Gener8ions Church Location"
              ></iframe>
            </div>
          </motion.div>
        </motion.div>

        <motion.div 
          className="visit-cta"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 variants={fadeInUp}>Ready to Visit?</motion.h2>
          <motion.p variants={fadeInUp}>
            Whether you're visiting for the first time or looking for a church home, you're welcome here! 
            We'd love to meet you and help you feel at home.
          </motion.p>
          <motion.div className="cta-buttons" variants={fadeInUp}>
            <Link to="/contact" className="btn btn-primary">Contact Us</Link>
            <Link to="/about" className="btn btn-outline">Learn More About Us</Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;

